import { useMemo } from 'react';

import {
    type ThermometerUpdateInput,
    useGetThermometersQuery,
    useGetDeliveryPeopleQuery,
    useUpdateThermometerMutation,
} from 'data/__generated__';

import { thermometersUpdatedOrCreatedHandler } from '../cacheHandlers/thermometersCacheHandler';
import { DataTable } from 'components/DataTable';
import { Loader } from 'components/Loader';
import { getThermometersColumns, type ThermometerRow } from '../constants/thermometersColumns';

export const ThermometersList = () => {
    const {
        loading: thermometersLoading,
        data: thermometersData,
        error: thermometersError,
    } = useGetThermometersQuery();

    const {
        loading: deliveryPeopleLoading,
        data: deliveryPeopleData,
        error: deliveryPeopleError,
    } = useGetDeliveryPeopleQuery();

    const [updateThermometer, { loading: updateLoading }] = useUpdateThermometerMutation();

    const deliveryPeopleOptions = useMemo(
        () =>
            (deliveryPeopleData?.getDeliveryPeople || [])
                .map(({ _id, profile: { fullname }, username }) => ({
                    value: _id,
                    label: `${fullname} (${username})`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [deliveryPeopleData],
    );

    async function update(input: ThermometerUpdateInput) {
        const { data } = await updateThermometer({
            variables: { thermometerUpdateInput: input },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la mise à jour du thermomètre');
        }

        thermometersUpdatedOrCreatedHandler([data.updateThermometer]);
    }

    const columns = useMemo(
        () => getThermometersColumns({ deliveryPeopleOptions, update, updateLoading }),
        [deliveryPeopleOptions, updateLoading],
    );

    if ((thermometersLoading && !thermometersData) || (deliveryPeopleLoading && !deliveryPeopleData)) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (thermometersError || !thermometersData || deliveryPeopleError || !deliveryPeopleData) {
        throw new Error('Une erreur est survenue lors de la récupération des thermomètres');
    }

    const sortedThermometers = [...thermometersData.getThermometers].sort((a, b) => a.number - b.number);

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<ThermometerRow>
                columns={columns}
                data={sortedThermometers}
                filterMode="client"
                getRowId={(row) => row._id}
            />
        </div>
    );
};
